import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

// project imports
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import ImpersonatePickerDialog from 'components/ImpersonatePickerDialog';
import useTour from 'hooks/useTour';
import { getImpersonationStatus } from 'services/api/impersonation';

import useAuth from 'hooks/useAuth';
import useOnlineStatus from 'hooks/useOnlineStatus';
import usePWAInstall from 'hooks/usePWAInstall';
import { useMyProfile } from 'hooks/api/useUsers';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import LoginOutlined from '@ant-design/icons/LoginOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import BulbOutlined from '@ant-design/icons/BulbOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import { DEFAULT_AVATAR } from 'utils/avatarDefaults';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();

  const { logout, user, isLoggedIn, isImpersonating } = useAuth();
  const { offlineUser } = useOnlineStatus();
  // Whether the API will accept an impersonation from this caller. Asked once,
  // rather than inferred from role names here — the rule (rbac_app_role
  // .can_impersonate plus an organization-scope check) lives on the server and
  // this menu should not hold a second, drifting copy of it.
  const [mayImpersonate, setMayImpersonate] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { startTour } = useTour();

  useEffect(() => {
    if (!isLoggedIn) {
      setMayImpersonate(false);
      return undefined;
    }
    let live = true;
    getImpersonationStatus()
      .then((res) => {
        if (live) setMayImpersonate(Boolean(res?.can_impersonate));
      })
      // Fail closed: no control rather than one that 403s.
      .catch(() => {
        if (live) setMayImpersonate(false);
      });
    return () => {
      live = false;
    };
  }, [isLoggedIn]);
  const { isInstallable, isInstalled, unavailableReason, installApp } = usePWAInstall();

  // Use online user or fallback to offline cached user
  const activeUser = user || offlineUser;

  // The uploaded profile picture lives on the self-service profile; fall back to
  // the default avatar when the user has none (or offline).
  const { data: profile } = useMyProfile();
  const avatarSrc = profile?.profile_image_url || DEFAULT_AVATAR;

  const handleLogin = () => {
    // Use replace so the protected URL doesn't sit in history.
    navigate('/login', { replace: true, state: { from: '' } });
  };

  const handleLogout = async () => {
    // Always attempt to clear client-side auth state, even when offline or when
    // `logout` is momentarily undefined — the local dispatch is what flips
    // isLoggedIn so AuthGuard can redirect. Backend failures are tolerated
    // inside JWTContext.logout already.
    try {
      if (logout) await logout();
    } catch (err) {
      console.warn('[Profile] logout threw:', err);
    }
    // Navigate to /login with replace so the back button doesn't return to a
    // protected page that will just bounce them out again.
    try {
      navigate('/login', { replace: true, state: { from: '' } });
    } catch (navErr) {
      console.warn('[Profile] navigate to /login failed, hard-redirecting:', navErr);
      window.location.replace('/login');
    }
  };

  // Get display name and role title. Prefer the self-service profile name so the
  // header reflects edits immediately (the auth-context user isn't refetched on
  // profile save); fall back to the auth-context name and existing defaults.
  const profileName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');
  const displayName =
    profileName ||
    (activeUser?.role === 'therapist' && (activeUser?.name === 'JWT User' || !activeUser?.name)
      ? 'Maya'
      : activeUser?.name || activeUser?.firstName || 'User');

  const getRoleTitle = (role) => {
    switch (role) {
      case 'supervisor':
        return intl.formatMessage({ id: 'profile.role.supervisor' });
      case 'therapist':
        return intl.formatMessage({ id: 'profile.role.therapist' });
      default:
        return intl.formatMessage({ id: 'profile.role.default' });
    }
  };
  const roleTitle = getRoleTitle(activeUser?.role);

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }} data-tour="header-profile">
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? 'grey.100' : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 },
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent', '&:hover': { bgcolor: 'secondary.light' } })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
          <Avatar alt="profile user" src={avatarSrc} size="sm" />
          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
            {displayName}
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              data-tour="profile-menu"
              sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar alt="profile user" src={avatarSrc} sx={{ width: 32, height: 32 }} />
                          <Stack>
                            <Typography variant="h6">{displayName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {roleTitle}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip
                          title={isLoggedIn ? intl.formatMessage({ id: 'profile.logout' }) : intl.formatMessage({ id: 'profile.login' })}
                        >
                          <IconButton size="large" sx={{ color: 'text.primary' }} onClick={isLoggedIn ? handleLogout : handleLogin}>
                            {isLoggedIn ? <LogoutOutlined /> : <LoginOutlined />}
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  {/* IMPERSONATE. Lives here rather than on User Management
                      because it is an act of the SESSION, not of that screen —
                      and because a supervisor granted the ability need not have
                      User Management at all. Hidden outright when the API says
                      this account cannot impersonate: a button that can only
                      403 is worse than no button. */}
                  {mayImpersonate && !isImpersonating && (
                    <Box sx={{ px: 2.5, pb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="warning"
                        startIcon={<EyeOutlined />}
                        onClick={() => {
                          setPickerOpen(true);
                          setOpen(false);
                        }}
                      >
                        {intl.formatMessage({ id: 'impersonation.pick.title' })}
                      </Button>
                    </Box>
                  )}

                  {/* GUIDED TOUR. The header icon is easy to miss and easier to
                      forget; someone who wants the tour a second time looks for
                      it where the rest of "about my account" lives. */}
                  <Box sx={{ px: 2.5, pb: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="secondary"
                      startIcon={<BulbOutlined />}
                      onClick={() => {
                        startTour();
                        setOpen(false);
                      }}
                    >
                      {intl.formatMessage({ id: 'profile.guidedTour' })}
                    </Button>
                  </Box>

                  {/* Install App Button - Show different states */}
                  <Box sx={{ px: 2.5, pb: 2 }}>
                    {isInstalled ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircleOutlined />}
                        disabled
                        sx={{
                          '&.Mui-disabled': {
                            borderColor: 'success.main',
                            color: 'success.main',
                            opacity: 0.8
                          }
                        }}
                      >
                        {intl.formatMessage({ id: 'profile.appInstalled' })}
                      </Button>
                    ) : isInstallable ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadOutlined />}
                        onClick={() => {
                          installApp();
                          setOpen(false);
                        }}
                      >
                        {intl.formatMessage({ id: 'profile.installApp' })}
                      </Button>
                    ) : (
                      // Disabled, but never silently: a grey button with no
                      // explanation is what made this look broken. The tooltip
                      // says which of the two reasons applies - the browser has
                      // no install prompt at all, or it has not offered one yet.
                      <Tooltip
                        title={intl.formatMessage({
                          id: unavailableReason === 'unsupported' ? 'profile.installUnsupported' : 'profile.installNotReady'
                        })}
                      >
                        <span>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="inherit"
                            startIcon={<DownloadOutlined />}
                            disabled
                            sx={{ opacity: 0.6 }}
                          >
                            {intl.formatMessage({ id: 'profile.installApp' })}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          '& .MuiTab-icon': {
                            marginBottom: 0
                          }
                        }}
                        icon={<UserOutlined />}
                        label={intl.formatMessage({ id: 'profile.profileTab' })}
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          '& .MuiTab-icon': {
                            marginBottom: 0
                          }
                        }}
                        icon={<SettingOutlined />}
                        label={intl.formatMessage({ id: 'profile.settingTab' })}
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab handleLogout={isLoggedIn ? handleLogout : handleLogin} isLoggedIn={isLoggedIn} />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* Outside the Popper: the menu closes as the dialog opens, and a dialog
          unmounted with its parent would vanish mid-flow. */}
      <ImpersonatePickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </Box>
  );
}

TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number, index: PropTypes.number, other: PropTypes.any };
